import { GRAVITEE_AM_BASE_URL, GRAVITEE_AM_CLIENT_ID, GRAVITEE_AM_REDIRECT_URI, HOST_MB } from '@/lib/constants'
import { PASS_ORIGIN } from '@/lib/api/pass-origin'

import { generatePKCE } from '@/lib/auth/pkce'

/** Réponse de PASS à l'échange du code d'autorisation. */
export interface GraviteeTokenResponse {
    authenticated?: boolean
    id_token?: string
    redirect_to?: string
    message_footer?: string | null
    message_warning?: string | null
    img_footer?: string | null
    user?: { whitelabel?: number | string }
    [key: string]: unknown
}

const PKCE_STORAGE_KEY = 'gravitee_pkce_verifier'
const STATE_STORAGE_KEY = 'gravitee_oauth_state'

const PASS_BASE_URL = PASS_ORIGIN

/**
 * Initie le flow OAuth2 Authorization Code + PKCE
 * Redirige l'utilisateur vers la page de login Gravitee AM
 */
export async function initiateGraviteeLogin({ forceLogin = false }: { forceLogin?: boolean } = {}): Promise<void> {
    const { codeVerifier, codeChallenge } = await generatePKCE()

    // Générer un state anti-CSRF (RFC 6749 §10.12)
    const stateBytes = new Uint8Array(32)
    crypto.getRandomValues(stateBytes)
    const state = btoa(String.fromCharCode(...stateBytes))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')

    // Stocker le code_verifier et le state pour vérification au callback
    sessionStorage.setItem(PKCE_STORAGE_KEY, codeVerifier)
    sessionStorage.setItem(STATE_STORAGE_KEY, state)

    const params = new URLSearchParams({
        client_id: GRAVITEE_AM_CLIENT_ID ?? '',
        response_type: 'code',
        redirect_uri: GRAVITEE_AM_REDIRECT_URI,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        scope: 'openid full_profile',
        state,
        owner_belair_id: String(HOST_MB),
        provider: 'customers',
    })

    // Après un logout, forcer la re-saisie des identifiants
    // même si la session SSO Gravitee AM est encore active
    if (forceLogin) {
        params.set('prompt', 'login')
    }

    window.location.href = `${GRAVITEE_AM_BASE_URL}/oauth/authorize?${params.toString()}`
}

/**
 * Échange le code d'autorisation contre des tokens.
 * L'appel passe par PASS qui :
 * - Ajoute le client_secret côté serveur
 * - Stocke access_token et refresh_token dans des cookies HttpOnly
 * - Retourne uniquement { authenticated: true, id_token }
 */
export async function exchangeCodeForTokens(code: string, returnedState: string | null): Promise<GraviteeTokenResponse> {
    // Vérification du state anti-CSRF (RFC 6749 §10.12)
    const expectedState = sessionStorage.getItem(STATE_STORAGE_KEY)
    sessionStorage.removeItem(STATE_STORAGE_KEY)

    if (!expectedState || expectedState !== returnedState) {
        throw new Error('State OAuth invalide — possible attaque CSRF')
    }

    const codeVerifier = sessionStorage.getItem(PKCE_STORAGE_KEY)
    sessionStorage.removeItem(PKCE_STORAGE_KEY)

    if (!codeVerifier) {
        throw new Error('Code verifier PKCE introuvable')
    }

    const body = new URLSearchParams({
        grant_type: 'authorization_code',
        code,
        redirect_uri: GRAVITEE_AM_REDIRECT_URI,
        client_id: GRAVITEE_AM_CLIENT_ID ?? '',
        code_verifier: codeVerifier,
        // Permet à PASS de calculer un redirect cross-brand sur ce flow MFA
        // (mêmes règles que le flow PASS classique /oauth/token). Gravitee
        // reçoit owner_belair_id dans /oauth/authorize mais ne le propage pas
        // au callback ni dans l'id_token, donc on le re-passe ici.
        owner_belair_id: String(HOST_MB),
    })

    const response = await fetch(`${PASS_BASE_URL}/api/v2/auth/gravitee/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        credentials: 'include',
        body: body.toString(),
    })

    if (!response.ok) {
        const error = await response.text()
        throw new Error(`Erreur échange token: ${response.status} - ${error}`)
    }

    return response.json() as Promise<GraviteeTokenResponse>
}

/**
 * Rafraîchit l'access_token via le refresh_token stocké dans un cookie HttpOnly.
 * L'appel passe par PASS qui lit le refresh_token depuis le cookie.
 */
export async function refreshGraviteeToken(): Promise<unknown> {
    const body = new URLSearchParams({
        client_id: GRAVITEE_AM_CLIENT_ID ?? '',
    })

    try {
        const response = await fetch(`${PASS_BASE_URL}/api/v2/auth/gravitee/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            credentials: 'include',
            body: body.toString(),
        })

        if (!response.ok) {
            return null
        }

        return (await response.json()) as unknown
    } catch {
        return null
    }
}

/**
 * Supprime les cookies de tokens Gravitee AM côté PASS
 */
export async function logoutGraviteeTokens(): Promise<void> {
    try {
        await fetch(`${PASS_BASE_URL}/api/v2/auth/gravitee/logout`, {
            method: 'POST',
            credentials: 'include',
        })
    } catch {
        // Silencieux — on continue le logout même si ça échoue
    }
}

/**
 * URL de déconnexion Gravitee AM
 *
 * `invalidate_tokens` n'est pas cosmétique : sans lui Gravitee ferme la session
 * SSO mais laisse valides les tokens déjà émis, qui restent acceptés par PASS via
 * l'introspection (VE-11).
 */
export function getGraviteeLogoutUrl(postLogoutRedirectUri?: string, idToken?: string): string {
    const redirectUri = postLogoutRedirectUri || window.location.origin + '/login'
    const params = new URLSearchParams({
        client_id: GRAVITEE_AM_CLIENT_ID ?? '',
        target_url: redirectUri,
        invalidate_tokens: 'true',
    })

    if (idToken) {
        params.set('id_token_hint', idToken)
    }

    return `${GRAVITEE_AM_BASE_URL}/logout?${params.toString()}`
}
